import { useState, useMemo, useEffect } from "react";
import { Filter, Search, Trash2 } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../../../styles/__labTechnicianManagement.module.scss";
import dialogStyles from "../../../styles/__labDialogAddEditTest.module.scss";
import user from "../../../assets/images/user.png";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { IoAddCircleOutline } from "react-icons/io5";
import LabDialogAddEditTechnical from "../../../components/lab-dialog-AddEditTechnical/LabDialogAddEditTechnical";
import LabDialogViewTechnical from "../../../components/lab-dialog-ViewTechnical/LabDialogViewTechnical";
import {
  getLabTechnicians,
  deleteLabTechnician,
} from "../../../services/labTechniciansService";
import { toast } from "../../../components/toast";

const TechnicianManagement = ({ onLogout, onNavigateToDashboard }) => {
  const { t, i18n } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
  const [openDialogAddEditTechnical, setOpenDialogAddEditTechnical] =
    useState(false);
  const [dialogType, setDialogType] = useState("add");
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [openDialogViewTechnical, setOpenDialogViewTechnical] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  // Delete-technician confirmation modal state
  const [technicianToDelete, setTechnicianToDelete] = useState(null);
  const [deletingTechnician, setDeletingTechnician] = useState(false);
  const dispatch = useDispatch();
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Fetch technicians from backend
  const fetchTechniciansData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Delete is a soft delete on the backend (status -> INACTIVE) and the
      // Active/Inactive tabs and status toggle are hidden, so the "All" list
      // shows ACTIVE technicians only; otherwise deleted ones stay visible.
      params.status = activeTab === "All" ? "ACTIVE" : activeTab.toUpperCase();

      const response = await getLabTechnicians(params);

      if (response && response.status) {
        setTechnicians(response.data?.technicians || []);
        setTotalPages(response.data?.pagination?.totalPages || 1);
      } else {
        setTechnicians([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching lab technicians:", error);
      toast.error(t("lab.technicianManagement.fetchError"));
      setTechnicians([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Fetch technicians on component mount and when filters change
  useEffect(() => {
    fetchTechniciansData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab]);

  const handleAddTechnician = () => {
    setDialogType("add");
    setSelectedTechnician(null);
    setOpenDialogAddEditTechnical(true);
  };

  const handleEditTechnician = (technician) => {
    setDialogType("edit");
    setSelectedTechnician(technician);
    setOpenDialogAddEditTechnical(true);
  };

  const handleViewTechnician = (technician) => {
    setSelectedTechnician(technician);
    setOpenDialogViewTechnical(true);
  };

  // Open the confirmation modal instead of the native confirm dialog.
  const handleDeleteTechnician = (technician) => {
    setTechnicianToDelete(technician);
  };

  const closeDeleteModal = () => {
    if (deletingTechnician) return;
    setTechnicianToDelete(null);
  };

  const confirmDeleteTechnician = async () => {
    if (!technicianToDelete?.id) return;
    setDeletingTechnician(true);
    try {
      await deleteLabTechnician(technicianToDelete.id);
      toast.success(t("lab.technicianManagement.deleteSuccess"));
      setTechnicianToDelete(null);

      // If the last row on a page was removed, step back a page (the effect
      // on currentPage refetches); otherwise refresh the current page.
      if (technicians.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchTechniciansData();
      }
    } catch (error) {
      console.error("Error deleting technician:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        t("lab.technicianManagement.deleteError");
      toast.error(errorMessage);
    } finally {
      setDeletingTechnician(false);
    }
  };

  const handleTechnicianSaved = () => {
    fetchTechniciansData(); // Refresh the list after save
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchTechniciansData();
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const resetFilters = () => {
    // Reset state values first
    setSearchTerm("");
    setStatusFilter("All");
    setDate(null);
    setCurrentPage(1);
    setActiveTab("All"); // Also reset the active tab

    // Fetch with reset values
    fetchTechniciansData();
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-payment";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";
      case "Pending":
        return "status-cod";
      case "COD":
        return "status-cod";
      case "Payment Complete":
        return "status-payment";
      case "Collected":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Home Visit":
        return "status-labtype";
      case "Imaging":
        return "status-imagingtype";
      case "In-Progress":
        return "status-imagingtype";
      case "On-Site":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      case "Technician Dispatched":
        return "status-dispatched";
      case "Sent":
        return "status-dispatched";
      case "Awaiting Payment":
        return "status-awaiting";
      case "Order Accepted":
        return "status-accepted";
      case "Active":
      case "ACTIVE":
        return "status-payment"; // Green badge for Active
      case "Inactive":
      case "INACTIVE":
        return "status-cancelled"; // Red badge for Inactive
      default:
        return "";
    }
  };

  const getTabTranslationKey = (tabName) => {
    const tabKeyMap = {
      All: "all",
      Active: "active",
      Inactive: "inactive",
    };
    return `lab.technicianManagement.tabs.${tabKeyMap[tabName] || tabName.toLowerCase()}`;
  };

  // Active / Inactive tabs hidden per request — only "All" is shown.
  const orderManagementTabs = ["All"];

  return (
    <LabDashboardLayout
      currentPage="lab_technicianmanagement"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <LabDialogAddEditTechnical
        open={openDialogAddEditTechnical}
        onClose={() => {
          setOpenDialogAddEditTechnical(false);
          setSelectedTechnician(null);
        }}
        type={dialogType}
        selectedTechnician={selectedTechnician}
        children={undefined}
        onSuccess={handleTechnicianSaved}
      />
      <LabDialogViewTechnical
        open={openDialogViewTechnical}
        onClose={() => {
          setOpenDialogViewTechnical(false);
          setSelectedTechnician(null);
        }}
        onEdit={(technician) => {
          // Close the Technician Info dialog and open the Edit dialog for the
          // same technician so the two modals never overlap.
          setOpenDialogViewTechnical(false);
          handleEditTechnician(technician || selectedTechnician);
        }}
        technician={selectedTechnician}
        children={undefined}
      />

      {showDetails ? (
        <AppointmentDetails
          handleBack={() => setShowDetails(false)}
          homeVisit={false}
          selectedAppointment={selectedAppointment}
        />
      ) : (
        <>
          <div className={styles["apt-page-header"]}>
            <h1>{t("lab.technicianManagement.title")}</h1>
            <button
              className={styles["apt-register-btn"]}
              onClick={handleAddTechnician}
            >
              <IoAddCircleOutline />{" "}
              {t("lab.technicianManagement.addNewTechnician")}
            </button>
          </div>
          <div className={styles["apt-appointments-section"]}>
            {/* Filters */}
            <div className={styles["apt-filters"]}>
              <div className={styles["apt-filter-group"]}>
                {/* <span className={styles["apt-filter-icon-wrap"]}>
                  <Filter className={styles["apt-filter-icon"]} />
                </span> */}
                <div className={styles["apt-search-wrapper"]}>
                  <Search className={styles["apt-search-icon"]} />
                  <input
                    type="text"
                    placeholder={t(
                      "lab.technicianManagement.filters.searchPlaceholder",
                    )}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles["apt-search-input"]}
                  />
                </div>
                <button
                  className={styles["apt-apply-btn"]}
                  onClick={handleApplyFilters}
                >
                  {t("lab.technicianManagement.filters.apply")}
                </button>
                <button
                  className={styles["apt-reset-btn"]}
                  onClick={resetFilters}
                >
                  {t("lab.technicianManagement.filters.clearSearch")}
                </button>
              </div>
            </div>

            <div className={styles["apt-appointment-tabs"]}>
              {orderManagementTabs.map((tabItem, idx) => {
                return (
                  <button
                    key={idx}
                    className={`${styles["apt-tab"]} ${
                      activeTab === tabItem ? styles["active"] : ""
                    }`}
                    onClick={() => {
                      setActiveTab(tabItem);
                      setStatusFilter(tabItem);
                      setCurrentPage(1);
                    }}
                  >
                    {t(getTabTranslationKey(tabItem))}
                  </button>
                );
              })}
            </div>

            {/* Appointments Table */}
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>{t("lab.technicianManagement.table.serviceCode")}</th>
                    <th>
                      {t("lab.technicianManagement.table.technicianName")}
                    </th>
                    <th>{t("lab.technicianManagement.table.gender")}</th>
                    <th>
                      {t("lab.technicianManagement.table.currentAssignment")}
                    </th>
                    <th>{t("lab.technicianManagement.table.mobileNumber")}</th>
                    <th>{t("lab.technicianManagement.table.action")}</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {t("lab.technicianManagement.loading")}
                      </td>
                    </tr>
                  ) : technicians.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {t("lab.technicianManagement.noTechniciansFound")}
                      </td>
                    </tr>
                  ) : (
                    technicians.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td>{item.serviceCode || "-"}</td>
                        <td>
                          {item.name ||
                            `${item.fullName || ""} ${item.surname || ""}`.trim()}
                        </td>
                        <td>{item.gender || "-"}</td>
                        <td>
                          <div className={styles["truncateAssignment"]}>
                            {item.currentAssignment?.testTypes &&
                            Array.isArray(item.currentAssignment.testTypes)
                              ? item.currentAssignment.testTypes.join(", ")
                              : item.currentAssignment
                                ? t(
                                    "lab.technicianManagement.assignmentUnavailable",
                                  )
                                : t(
                                    "lab.technicianManagement.noCurrentAssignment",
                                    "No current assignment",
                                  )}
                          </div>
                        </td>
                        <td>{item.mobileNumber || "-"}</td>
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <button
                              className={styles["apt-action-btn"]}
                              title={t("lab.technicianManagement.actions.view")}
                              onClick={() => handleViewTechnician(item)}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M12 5C5 5 2 12 2 12s3 7 10 7 10-7 10-7-3-7-10-7z"
                                  stroke="#155DFC"
                                  strokeWidth="1.5"
                                />
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="3"
                                  stroke="#155DFC"
                                  strokeWidth="1.5"
                                />
                              </svg>
                            </button>
                            <button
                              className={styles["apt-action-btn"]}
                              onClick={() => handleEditTechnician(item)}
                              title={t("lab.technicianManagement.actions.edit")}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5.70809 19.8987C5.25793 19.8987 4.87743 19.7432 4.56659 19.4324C4.25576 19.1216 4.10034 18.7411 4.10034 18.2909V5.70641C4.10034 5.25624 4.25576 4.87574 4.56659 4.56491C4.87743 4.25407 5.25793 4.09866 5.70809 4.09866H12.3351C12.5518 4.09866 12.7143 4.16674 12.8226 4.30291C12.9309 4.43924 12.9851 4.58782 12.9851 4.74866C12.9851 4.90949 12.9307 5.05807 12.8218 5.19441C12.713 5.33057 12.5539 5.39866 12.3446 5.39866H5.70809C5.63109 5.39866 5.56059 5.43074 5.49659 5.49491C5.43243 5.55891 5.40034 5.62941 5.40034 5.70641V18.2909C5.40034 18.3679 5.43243 18.4384 5.49659 18.5024C5.56059 18.5666 5.63109 18.5987 5.70809 18.5987H18.2926C18.3696 18.5987 18.4401 18.5666 18.5041 18.5024C18.5683 18.4384 18.6003 18.3679 18.6003 18.2909V12.2037C18.6003 11.9952 18.6684 11.8367 18.8046 11.7284C18.9409 11.6201 19.0895 11.5659 19.2503 11.5659C19.4112 11.5659 19.5598 11.6201 19.6961 11.7284C19.8323 11.8367 19.9003 11.9992 19.9003 12.2159V18.2909C19.9003 18.7411 19.7449 19.1216 19.4341 19.4324C19.1233 19.7432 18.7428 19.8987 18.2926 19.8987H5.70809ZM10.1003 13.0994V11.5722C10.1003 11.359 10.1388 11.1537 10.2158 10.9564C10.2927 10.7589 10.4103 10.581 10.5688 10.4227L18.8351 2.15641C18.9734 2.01791 19.1189 1.92141 19.2716 1.86691C19.4241 1.81241 19.584 1.78516 19.7513 1.78516C19.9208 1.78516 20.0838 1.81241 20.2403 1.86691C20.397 1.92141 20.5408 2.01291 20.6718 2.14141L21.8041 3.24866C21.9489 3.38716 22.0518 3.53899 22.1128 3.70416C22.1737 3.86932 22.2041 4.03674 22.2041 4.20641C22.2041 4.37641 22.1751 4.54099 22.1171 4.70016C22.0591 4.85932 21.9612 5.00749 21.8233 5.14466L13.5283 13.4399C13.3698 13.5982 13.1923 13.7143 12.9958 13.7882C12.7992 13.8618 12.5946 13.8987 12.3821 13.8987H10.9088C10.6813 13.8987 10.4898 13.8225 10.3341 13.6702C10.1783 13.5177 10.1003 13.3274 10.1003 13.0994ZM11.4003 12.5987H12.5216L18.7291 6.39091L18.1811 5.81791L17.5888 5.25066L11.4003 11.4392V12.5987Z"
                                  fill="#3B3B48"
                                />
                              </svg>
                            </button>
                            <button
                              className={styles["apt-action-btn"]}
                              onClick={() => handleDeleteTechnician(item)}
                              title={t(
                                "lab.technicianManagement.actions.delete",
                              )}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7.30775 20.4997C6.81058 20.4997 6.385 20.3227 6.031 19.9687C5.677 19.6147 5.5 19.1892 5.5 18.692V5.99973H5.25C5.0375 5.99973 4.85942 5.92782 4.71575 5.78398C4.57192 5.64015 4.5 5.46198 4.5 5.24948C4.5 5.03682 4.57192 4.85873 4.71575 4.71523C4.85942 4.57157 5.0375 4.49973 5.25 4.49973H9C9 4.2549 9.08625 4.04624 9.25875 3.87374C9.43108 3.7014 9.63967 3.61523 9.8845 3.61523H14.1155C14.3603 3.61523 14.5689 3.7014 14.7413 3.87374C14.9138 4.04624 15 4.2549 15 4.49973H18.75C18.9625 4.49973 19.1406 4.57165 19.2843 4.71548C19.4281 4.85932 19.5 5.03748 19.5 5.24998C19.5 5.46265 19.4281 5.64073 19.2843 5.78423C19.1406 5.9279 18.9625 5.99973 18.75 5.99973H18.5V18.692C18.5 19.1892 18.323 19.6147 17.969 19.9687C17.615 20.3227 17.1894 20.4997 16.6923 20.4997H7.30775ZM17 5.99973H7V18.692C7 18.7818 7.02883 18.8556 7.0865 18.9132C7.14417 18.9709 7.21792 18.9997 7.30775 18.9997H16.6923C16.7821 18.9997 16.8558 18.9709 16.9135 18.9132C16.9712 18.8556 17 18.7818 17 18.692V5.99973ZM10.1543 16.9997C10.3668 16.9997 10.5448 16.9279 10.6885 16.7842C10.832 16.6404 10.9037 16.4622 10.9037 16.2497V8.74973C10.9037 8.53723 10.8318 8.35907 10.688 8.21523C10.5443 8.07157 10.3662 7.99973 10.1535 7.99973C9.941 7.99973 9.76292 8.07157 9.61925 8.21523C9.47575 8.35907 9.404 8.53723 9.404 8.74973V16.2497C9.404 16.4622 9.47583 16.6404 9.6195 16.7842C9.76333 16.9279 9.94158 16.9997 10.1543 16.9997ZM13.8465 16.9997C14.059 16.9997 14.2371 16.9279 14.3807 16.7842C14.5243 16.6404 14.596 16.4622 14.596 16.2497V8.74973C14.596 8.53723 14.5242 8.35907 14.3805 8.21523C14.2367 8.07157 14.0584 7.99973 13.8458 7.99973C13.6333 7.99973 13.4552 8.07157 13.3115 8.21523C13.168 8.35907 13.0962 8.53723 13.0962 8.74973V16.2497C13.0962 16.4622 13.1682 16.6404 13.312 16.7842C13.4557 16.9279 13.6338 16.9997 13.8465 16.9997Z"
                                  fill="#EC003F"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={styles["apt-pagination"]}>
              <div className={styles["apt-pagination-info"]}>
                {t("lab.technicianManagement.pagination.showing")}{" "}
                {(currentPage - 1) * itemsPerPage + 1}{" "}
                {t("lab.technicianManagement.pagination.to")}{" "}
                {(currentPage - 1) * itemsPerPage + technicians.length}{" "}
                {t("lab.technicianManagement.pagination.of")}{" "}
                {totalPages * itemsPerPage}
              </div>
              <div className={styles["apt-pagination-controls"]}>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    currentPage === 1 ? styles["disabled"] : ""
                  }`}
                  // onClick={() => handlePageChange(currentPage - 1)}
                  // onClick={() => handlePageChange11(currentPage)}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t("lab.technicianManagement.pagination.previous")}
                </button>
                <div className={styles["apt-page-numbers"]}>
                  {/* {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    className={`${styles["apt-page-btn"]} ${
                      styles[`${currentPage === page ? "active" : ""}`]
                    }`}
                    onClick={() => handlePageChange(currentPage)}
                  >
                    {page}
                  </button>
                );
              })} */}

                  {Array.from({ length: totalPages }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`${styles["apt-page-btn"]} ${
                          styles[`${currentPage === page ? "active" : ""}`]
                        }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    styles["apt-next-btn"]
                  } ${currentPage === totalPages ? styles["disabled"] : ""}`}
                  // onClick={() => handlePageChange(currentPage + 1)}
                  // onClick={() => handlePageChange1(currentPage)}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t("lab.technicianManagement.pagination.next")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Technician Confirmation Modal — same style as the Delete Test modal */}
      {technicianToDelete && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-technician-title"
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={closeDeleteModal}
                disabled={deletingTechnician}
                aria-label={t("lab.technicianManagement.cancel", "Cancel")}
              >
                ×
              </button>
              <h2
                id="delete-technician-title"
                className={dialogStyles["dialog-title"]}
              >
                {t("lab.technicianManagement.deleteTitle", "Delete Technician")}
              </h2>
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {t(
                  "lab.technicianManagement.confirmDelete",
                  "Are you sure you want to delete this technician?",
                )}
                {technicianToDelete.name ? (
                  <>
                    <br />
                    <strong style={{ color: "#111827" }}>
                      {technicianToDelete.name}
                    </strong>
                  </>
                ) : null}
              </p>
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={closeDeleteModal}
                  disabled={deletingTechnician}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  {t("lab.technicianManagement.cancel", "Cancel")}
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={confirmDeleteTechnician}
                  disabled={deletingTechnician}
                  style={{
                    opacity: deletingTechnician ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  <Trash2 size={16} />{" "}
                  {deletingTechnician
                    ? t("lab.technicianManagement.deleting", "Deleting...")
                    : t("lab.technicianManagement.actions.delete", "Delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LabDashboardLayout>
  );
};

export default TechnicianManagement;
